import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Select, AutoComplete, DatePicker, message } from 'antd';
import dayjs from 'dayjs';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import LocationCascade from '../../components/LocationCascade';
import PartyLocationDropdown from '../admin/createUser/PartyLocationDropdown';
import AssignTaskModal, { statusBadge, type SubTask } from './AssignTaskModal';
import {
  useGetUsersQuery,
  useLazyGetUserByPhoneQuery,
  useLazyGetUserQuery,
  userName,
  userDesignationList,
} from '../../features/users/usersApi';
import { useUploadS3Mutation } from '../../features/uploads/uploadApi';
import {
  useGetMyTreeQuery,
  useGetOrganizationsQuery,
} from '../../features/organizations/organizationsApi';
import { useGetTeamsQuery, teamName, teamUserIds, type Team } from '../../features/teams/teamsApi';
import {
  useGetReferencePersonsQuery,
  useGetReferencePersonDesignationsQuery,
  referencePersonName,
  referencePersonKey,
  referencePersonPhones,
} from '../../features/dailyDirections/dailyDirectionApi';
import {
  useGetCitizenRequestQuery,
  useGetCitizenRequestsQuery,
  useCreateCitizenRequestMutation,
  useUpdateCitizenRequestMutation,
  useLazySearchOldCrQuery,
  useSearchRelatedCrQuery,
  useGetCrCategoriesQuery,
  crTaskSubCategory,
  type CrAsset,
  type CitizenRequest,
} from '../../features/citizenRequests/citizenRequestApi';
import {
  PersonIcon,
  CrDescDocIcon,
  CrUploadCloudIcon,
  CrTeamIcon,
  DdReferenceIcon,
  CrLocationIcon,
  AssignmentIcon,
  PlusIcon,
  TrashIcon,
  SearchIcon,
  LinkIcon,
  InfoCircleIcon,
  CaretIcon,
  ClearFilterIcon,
  DirCalendarIcon,
  DdClockIcon,
  CrEditIcon,
  CrDeleteIcon,
  ArrowLeftIcon,
} from '../../assets/icons';

/* Shared field styles — matches the Figma light-blue input treatment. */
const INPUT =
  'w-full rounded-md border border-[#e2e8f0] bg-[#eef4ff] px-3 py-3 text-sm text-ink placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-brand/15';
const LABEL = 'text-xs font-medium text-[#64748b]';
/* Small uppercase form label used on the step-2 task card. */
const FLABEL = 'text-[11px] font-bold uppercase tracking-[0.55px] text-[#5b6b79]';

const STEPS = ['Basic Details', 'Issue Details', 'Assignment', 'Review'] as const;

const CATEGORIES = ['Infrastructure', 'Water Supply', 'Roads', 'Health', 'Education'];
const ASSIGNEES = ['Dept. of Public Works', 'Dept. of Health', 'Dept. of Education', 'Municipal Office'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];
/** Priority pill colours (Step 3 assignment cards). */
const PRIORITY_TONE: Record<string, string> = {
  Low: 'bg-[#f1f5f9] text-[#475569]',
  Medium: 'bg-[#fffbeb] text-[#b45309]',
  High: 'bg-[#fef2f2] text-[#dc2626]',
  Critical: 'bg-[#fef2f2] text-[#b91c1c]',
};
const REFERENCE_SCOPES = [
  { key: 'inside', label: 'Within Constituency' },
  { key: 'outside', label: 'Outside Constituency' },
];

/** Issue-form validation (Step 2). Priority/assignment move to the Assignment step. */
const TASK_SCHEMA = Yup.object({
  location: Yup.string().required('Required'),
  category: Yup.string().required('Required'),
  subCategory: Yup.string().required('Required'),
  description: Yup.string().trim().required('Required'),
  priority: Yup.string(),
});

/** Step 1 (Basic Details) validation — Requestor Information. Required fields follow the
 * design's asterisks: First Name, Last Name, Phone Number. Email/Address/Date are optional. */
const STEP1_SCHEMA = Yup.object({
  firstName: Yup.string().trim().required('First name is required.'),
  lastName: Yup.string().trim().required('Last name is required.'),
  email: Yup.string().trim().email('Enter a valid email address.'),
  phone: Yup.string()
    .trim()
    .required('Phone number is required.')
    .matches(/^[+\d][\d\s-]{7,}$/, 'Enter a valid phone number.'),
  receivedDate: Yup.string(),
  // description now lives on the Issue Details step; validated in handleNext when leaving it.
  description: Yup.string(),
});

/** A member option in the assign dropdowns (real user id + display name). */
interface Member {
  id: number;
  name: string;
}

/** A team entry — one may be flagged primary. */
interface TeamEntry {
  id: number;
  primary?: boolean;
}

/** A task added on Step 2 — carries ids for the API plus a display label for the table. */
interface Task {
  id: number; // local row id
  status: string; // task status (e.g. To-Do)
  category: string;
  subCategory: string;
  priority: string;
  description: string;
  location: string;
  dueDate: string; // YYYY-MM-DD
  memberIds: number[]; // assignMembers (Individual)
  teams: TeamEntry[];
  refCr: string;
  isDuplicate: boolean;
  assignLabel: string; // resolved names for the table
  subTasks: SubTask[]; // Step-3 assigned sub-tasks (UI-only)
}

const TASK_FILTERS: { label: string; options: string[] }[] = [
  { label: 'Category', options: ['All', ...CATEGORIES] },
  { label: 'Date', options: ['All', 'Today', 'This Week', 'This Month'] },
  { label: 'Assigned To', options: ['All', ...ASSIGNEES] },
];

const emptyDraft = {
  location: '',
  description: '',
  category: '',
  subCategory: '',
  priority: '',
  dueDate: '',
  assignMembers: [] as number[],
  teams: [] as TeamEntry[],
  refCr: '',
  isDuplicate: false,
};

/** A titled card with a round icon header, matching the design's section cards. */
function SectionCard({
  icon,
  iconWrap,
  title,
  subtitle,
  action,
  children,
}: {
  icon: ReactNode;
  iconWrap: string;
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-[#e2e8f0] bg-white shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
      <div className="flex items-center gap-3 rounded-t-xl border-b border-[#e2e8f0] px-6 py-4">
        <span className={['flex shrink-0 items-center justify-center rounded-full', iconWrap].join(' ')}>
          {icon}
        </span>
        <div className="flex flex-col">
          <h2 className="text-lg font-semibold text-[#00173e]">{title}</h2>
          {subtitle && <p className="text-sm text-[#424655]">{subtitle}</p>}
        </div>
        {action && <div className="ml-auto">{action}</div>}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

/**
 * "Select Team" dropdown (Figma node 1262-8): lists teams as radio rows; expanding a
 * team reveals its members with checkboxes and a per-member "Make Primary" pill.
 * Selected members (across teams) are held as {id, primary?} entries.
 */
function TeamSelect({
  teams,
  selectedIds,
  primaryId,
  onToggle,
  onMakePrimary,
  placeholder,
  resolveName,
}: {
  teams: Team[];
  selectedIds: number[];
  primaryId?: number;
  onToggle: (id: number) => void;
  onMakePrimary: (id: number) => void;
  placeholder: string;
  resolveName: (id: number) => string;
}) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  // Prefer the names the teams endpoint returns; fall back to the parent resolver.
  const nameMap = useMemo(() => {
    const m: Record<number, string> = {};
    teams.forEach((t) =>
      (Array.isArray(t.members) ? t.members : []).forEach((u) => {
        if (u?.id != null) m[u.id] = u.full_name || u.name || '';
      }),
    );
    return m;
  }, [teams]);
  const nm = (id: number) => nameMap[id] || resolveName(id) || `User ${id}`;

  const membersOf = (t: Team): { id: number; name: string }[] => {
    if (Array.isArray(t.members) && t.members.length) {
      return t.members
        .filter((u) => u?.id != null)
        .map((u) => ({ id: u.id as number, name: u.full_name || u.name || `User ${u.id}` }));
    }
    return teamUserIds(t).map((id) => ({ id, name: nm(id) }));
  };

  const label = selectedIds.map(nm).filter(Boolean).join(', ');

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-11 w-full items-center justify-between gap-2 rounded-md border border-[#e2e8f0] bg-white px-3 text-left text-sm shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
      >
        <span className={label ? 'truncate text-ink' : 'text-[#6b7280]'}>{label || placeholder}</span>
        <CaretIcon className={['size-2.5 shrink-0 text-[#64748b] transition-transform', open ? 'rotate-180' : ''].join(' ')} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute inset-x-0 z-30 mt-1 overflow-hidden rounded-xl border border-[#e2e8f0] bg-white shadow-[0px_4px_16px_rgba(0,0,0,0.08)]">
            {/* Header */}
            <div className="flex items-center gap-1.5 border-b border-[#e2e8f0] px-5 py-2.5">
              <span className="flex size-5 items-center justify-center rounded-full bg-[#eff6ff] text-brand">
                <CrTeamIcon className="size-3" />
              </span>
              <span className="text-sm font-medium text-[#00173e]">Select Team</span>
            </div>

            <div className="flex max-h-[280px] flex-col gap-1.5 overflow-y-auto p-2">
              {teams.length === 0 && (
                <p className="px-1.5 py-2 text-xs text-ink-secondary">No teams available.</p>
              )}
              {teams.map((t) => {
                const isOpen = expanded === t.id;
                const members = isOpen ? membersOf(t) : [];
                return (
                  <div
                    key={t.id}
                    className={[
                      'rounded-md border',
                      isOpen ? 'border-brand/50' : 'border-[#46556f]/50',
                    ].join(' ')}
                  >
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : t.id)}
                      className={[
                        'flex w-full items-center gap-1.5 rounded-md px-1.5 py-1.5 text-left',
                        isOpen ? 'bg-[#eef4ff]' : '',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'flex size-3.5 shrink-0 items-center justify-center rounded-full border-[1.5px]',
                          isOpen ? 'border-brand' : 'border-[#46556f]/70',
                        ].join(' ')}
                      >
                        {isOpen && <span className="size-1.5 rounded-full bg-brand" />}
                      </span>
                      <span
                        className={[
                          'flex-1 truncate text-xs font-medium',
                          isOpen ? 'text-brand' : 'text-[#46556f]/80',
                        ].join(' ')}
                      >
                        {teamName(t)}
                      </span>
                      <CaretIcon
                        className={['size-3 shrink-0 transition-transform', isOpen ? 'rotate-180 text-brand' : 'text-[#46556f]/70'].join(' ')}
                      />
                    </button>

                    {isOpen && (
                      <div className="flex flex-col gap-0.5 px-1.5 pb-2 pt-1">
                        {members.length === 0 && (
                          <p className="px-1 py-1.5 text-[11px] text-ink-secondary">No members in this team.</p>
                        )}
                        {members.map((mem) => (
                          <div
                            key={mem.id}
                            className="flex h-7 items-center justify-between gap-2 rounded px-1 hover:bg-[#f8fafc]"
                          >
                            <label className="flex flex-1 cursor-pointer items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(mem.id)}
                                onChange={() => onToggle(mem.id)}
                                className="size-4 rounded accent-brand"
                              />
                              <span className="truncate text-[11px] font-medium text-[#46556f]">{mem.name}</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => onMakePrimary(mem.id)}
                              className={[
                                'shrink-0 rounded-full px-2.5 py-0.5 text-[8px] font-normal transition-colors',
                                primaryId === mem.id
                                  ? 'bg-brand text-white'
                                  : 'bg-[#eef4ff] text-brand hover:bg-[#dbe6ff]',
                              ].join(' ')}
                            >
                              {primaryId === mem.id ? 'Primary' : 'Make Primary'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** A read-only label/value pair used on the Review step. */
function ReviewField({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-bold uppercase tracking-[0.55px] text-[#424655]">{label}</span>
      {children ? (
        <div className="flex flex-col text-sm text-[#191b24]">{children}</div>
      ) : (
        <span className="text-sm text-[#191b24]">{value}</span>
      )}
    </div>
  );
}

export default function CreateCitizenRequestPage() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const isEdit = !!routeId;
  const [step, setStep] = useState(0);

  // Scroll back to the top whenever the wizard step changes (e.g. Step 1 → Step 2).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // Step 1 — requestor details (Formik + Yup).
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      receivedDate: '',
      address: '',
      district: '',
      description: '',
    },
    validationSchema: STEP1_SCHEMA,
    onSubmit: () => setStep(1),
  });
  const form = formik.values;
  const set = (k: keyof typeof form, v: string) => formik.setFieldValue(k, v);
  // Only surface an error once the field has been touched.
  const errors: Record<string, string> = {};
  (Object.keys(formik.errors) as (keyof typeof form)[]).forEach((k) => {
    if (formik.touched[k]) errors[k] = formik.errors[k] as string;
  });

  const [crCode, setCrCode] = useState('');

  // "Search Existing Citizen" — find a prior requestor and prefill their details.
  const [showCitizenSearch, setShowCitizenSearch] = useState(false);
  const [citizenSearch, setCitizenSearch] = useState('');
  const [citizenSearchQ, setCitizenSearchQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setCitizenSearchQ(citizenSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [citizenSearch]);
  const { data: citizenHits } = useGetCitizenRequestsQuery(
    { search: citizenSearchQ },
    { skip: !citizenSearchQ },
  );
  const prefillFromCitizen = (c: CitizenRequest) => {
    const d = (c.details ?? {}) as Record<string, unknown>;
    const str = (x: unknown) => (typeof x === 'string' ? x : '');
    const parts = (c.visitor_name || '').trim().split(/\s+/);
    formik.setFieldValue('firstName', str(d.firstName) || parts[0] || '');
    formik.setFieldValue('lastName', str(d.lastName) || parts.slice(1).join(' ') || '');
    formik.setFieldValue('email', str(d.email));
    formik.setFieldValue('phone', c.visitor_phone || str(d.phone) || '');
    formik.setFieldValue('address', c.visitor_address || str(d.address) || '');
    setShowCitizenSearch(false);
    setCitizenSearch('');
  };

  // Reference Person section (Step 1) — matches the Create Daily Direction design.
  const [refScope, setRefScope] = useState('inside');
  const [refDesignation, setRefDesignation] = useState('');
  const [refUserId, setRefUserId] = useState<number | ''>('');
  const [refUserSource, setRefUserSource] = useState('');
  const [refUserName, setRefUserName] = useState('');
  const [refLocation, setRefLocation] = useState('');
  // The matched reference person's directory — drives which Location picker to show.
  const [refDirectory, setRefDirectory] = useState('');
  const [refPhones, setRefPhones] = useState<string[]>(['']);
  const [pinnedRef, setPinnedRef] = useState<{ value: string; label: string } | null>(null);

  // Auto-fill the Reference Person from the first contact number (GET /access/users/by-phone/).
  // When a 10-digit number matches a directory user, its name/designation/location prefill
  // (all still editable). The last looked-up phone avoids re-firing on every render.
  const [runByPhone] = useLazyGetUserByPhoneQuery();
  const [runGetUser] = useLazyGetUserQuery();
  const lastLookedUp = useRef('');
  useEffect(() => {
    const phone = (refPhones[0] || '').replace(/\D/g, '');
    if (phone.length !== 10 || phone === lastLookedUp.current) return;
    const t = setTimeout(async () => {
      lastLookedUp.current = phone;
      try {
        const u = await runByPhone({ phone }).unwrap();
        if (!u) return;
        const nm = userName(u);
        setRefUserId(u.id);
        setRefUserSource('directory');
        setRefUserName(nm !== '—' ? nm : '');
        setRefDesignation(userDesignationList(u)[0] || '');
        const dir = u.directory || '';
        setRefDirectory(dir);
        // Party locations are org-node ids → key them "n:<id>" so the node tree resolves them.
        const rawLoc = u.location != null ? String(u.location) : '';
        setRefLocation(dir === 'party' && rawLoc ? `n:${rawLoc}` : rawLoc);
        setPinnedRef({ value: `directory-${u.id}`, label: nm !== '—' ? nm : `User ${u.id}` });
      } catch {
        /* no match — leave the fields for manual entry */
      }
    }, 400);
    return () => clearTimeout(t);
  }, [refPhones]);

  // Edit mode — load the existing request (GET /citizen-request/<id>/), which nests its tasks
  // (each with its subtasks) — used to prefill the whole wizard.
  const { data: editData } = useGetCitizenRequestQuery(routeId as string, { skip: !isEdit });

  // Assign-member options — organization-directory users (GET /access/users/?directory=organization).
  const { data: usersData, isFetching: usersLoading } = useGetUsersQuery({
    directory: 'organization',
    all: true,
  });
  const memberOptions: Member[] = useMemo(
    () =>
      (usersData?.items ?? []).map((u) => {
        const nm = userName(u);
        return { id: u.id, name: nm !== '—' ? nm : u.email || `User ${u.id}` };
      }),
    [usersData],
  );

  // Teams (with their members) for the "Select Team" dropdown.
  const { data: teamsData } = useGetTeamsQuery({ isActive: true, all: true });
  const teams = teamsData?.items ?? [];
  // id → name for team members, so labels resolve even when they aren't assignable users.
  const teamNameMap = useMemo(() => {
    const m: Record<number, string> = {};
    teams.forEach((t) =>
      (Array.isArray(t.members) ? t.members : []).forEach((u) => {
        if (u?.id != null) m[u.id] = u.full_name || u.name || '';
      }),
    );
    return m;
  }, [teams]);
  const nameOf = (id: number | '') =>
    (id === '' ? '' : memberOptions.find((m) => m.id === id)?.name || teamNameMap[id]) ?? '';
  // Resolve a task's assignee names live (member options may load after the tasks).
  const taskAssignLabel = (t: Task) => {
    const names = [...t.memberIds, ...t.teams.map((x) => x.id)].map(nameOf).filter(Boolean);
    return names.join(', ') || t.assignLabel || '—';
  };


  // Supporting evidence — upload to S3 on select, preview before submit.
  const [uploadS3] = useUploadS3Mutation();
  const [assets, setAssets] = useState<CrAsset[]>([]);
  const [uploading, setUploading] = useState(false);
  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded: CrAsset[] = [];
      for (const file of Array.from(files)) {
        const res = await uploadS3(file).unwrap();
        uploaded.push({ file_url: res.url, file_type: res.content_type || file.type, name: file.name });
      }
      setAssets((prev) => [...prev, ...uploaded]);
    } catch {
      message.error('File upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };
  const removeAsset = (idx: number) => setAssets((prev) => prev.filter((_, i) => i !== idx));
  const isImageAsset = (a: CrAsset) =>
    (a.file_type || '').startsWith('image') || /\.(png|jpe?g|gif|webp)$/i.test(a.file_url || '');

  // Step 2 — tasks are managed locally and sent with the request payload.
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskSearch, setTaskSearch] = useState('');
  const [taskFilters, setTaskFilters] = useState<Record<string, string>>(
    Object.fromEntries(TASK_FILTERS.map((f) => [f.label, 'All'])),
  );

  const [saving, setSaving] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [createRequest] = useCreateCitizenRequestMutation();
  const [updateRequest] = useUpdateCitizenRequestMutation();
  const [pendingTask, setPendingTask] = useState<Task | null>(null);

  // Task location — the org's EC location tree (/territory/ec-tree/), searchable.
  const { data: myTree } = useGetMyTreeQuery();
  // TEMP: default the user flow to the "Alwar BJP Test" org for now (falls back to
  // the signed-in user's own org). Resolved by name so it works across environments.
  const { data: orgsData } = useGetOrganizationsQuery({ all: true });
  const defaultOrg = orgsData?.items?.find(
    (o) => o.name?.trim().toLowerCase() === 'alwar bjp test',
  )?.id;
  const organization = defaultOrg ?? myTree?.organization ?? undefined;

  // Designation options for the Reference Person card (GET /reference-persons/designations/).
  const { data: refDesignationList } = useGetReferencePersonDesignationsQuery();
  const refDesignationOptions = useMemo(() => {
    const opts = (refDesignationList ?? []).map((v) => ({ value: v, label: v }));
    // Keep an auto-filled / custom designation visible even if it isn't in the list.
    if (refDesignation && !opts.some((o) => o.value === refDesignation)) {
      opts.unshift({ value: refDesignation, label: refDesignation });
    }
    return opts;
  }, [refDesignationList, refDesignation]);

  // Reference-person dropdown — org-scoped, server-side search (within-constituency case).
  const [refPersonInput, setRefPersonInput] = useState('');
  const [refPersonSearch, setRefPersonSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setRefPersonSearch(refPersonInput.trim()), 300);
    return () => clearTimeout(t);
  }, [refPersonInput]);
  const { data: refPersons } = useGetReferencePersonsQuery(
    { organization: organization!, ...(refPersonSearch ? { search: refPersonSearch } : {}) },
    { skip: organization == null },
  );
  const refPersonOptions = (refPersons ?? []).map((p) => ({
    value: referencePersonKey(p),
    label: referencePersonName(p),
    source: p.source ?? 'directory',
    refId: p.id,
    designation: p.designation ?? '',
    location: p.location ?? '',
    phones: referencePersonPhones(p),
  }));

  // Old CR Reference (per task) — live search of prior requests.
  const [crRefSearch, setCrRefSearch] = useState('');
  const [runCrRef, { data: crRefHits, isFetching: crRefLoading }] = useLazySearchOldCrQuery();
  useEffect(() => {
    const q = crRefSearch.trim();
    if (q.length < 2) return;
    const t = setTimeout(() => runCrRef({ q }), 300);
    return () => clearTimeout(t);
  }, [crRefSearch, runCrRef]);

  // Task draft is a Formik form; submit adds/updates the task in local state (sent with the CR).
  const taskFormik = useFormik({
    initialValues: emptyDraft,
    validationSchema: TASK_SCHEMA,
    onSubmit: (values, helpers) => {
      const assignLabel =
        [...values.assignMembers, ...values.teams.map((t) => t.id)]
          .map((id) => nameOf(id))
          .filter(Boolean)
          .join(', ') || '—';
      if (editingTaskId != null) {
        setTasks((prev) =>
          prev.map((t) =>
            t.id === editingTaskId
              ? {
                  ...t,
                  category: values.category,
                  subCategory: values.subCategory,
                  priority: values.priority,
                  description: values.description,
                  location: values.location,
                  dueDate: values.dueDate,
                  memberIds: values.assignMembers,
                  teams: values.teams,
                  refCr: values.refCr,
                  isDuplicate: values.isDuplicate,
                  assignLabel,
                }
              : t,
          ),
        );
        setEditingTaskId(null);
      } else {
        setTasks((prev) => [
          ...prev,
          {
            id: (prev.at(-1)?.id ?? 0) + 1,
            status: 'To-Do',
            category: values.category,
            subCategory: values.subCategory,
            priority: values.priority,
            description: values.description,
            location: values.location,
            dueDate: values.dueDate,
            memberIds: values.assignMembers,
            teams: values.teams,
            refCr: values.refCr,
            isDuplicate: values.isDuplicate,
            assignLabel,
            subTasks: [],
          },
        ]);
      }
      helpers.resetForm();
      setCrRefSearch('');
    },
  });
  const draft = taskFormik.values;
  const addingTask = taskFormik.isSubmitting;

  // Related / duplicate scan (Step 2 panel) — fired off a DEBOUNCED snapshot of all 4 criteria
  // (location + category + sub-category + text). Only once all four are present do we call the
  // API. Debouncing the whole snapshot means mid-edit transitions (e.g. changing the category
  // clears the sub-category, leaving only 3 values) never trigger a call.
  const [scan, setScan] = useState({ location: '', category: '', subCategory: '', desc: '' });
  useEffect(() => {
    const t = setTimeout(
      () =>
        setScan({
          location: draft.location || '',
          category: draft.category || '',
          subCategory: draft.subCategory || '',
          desc: draft.description.trim(),
        }),
      350,
    );
    return () => clearTimeout(t);
  }, [draft.location, draft.category, draft.subCategory, draft.description]);
  const canScanRelated =
    !!scan.location && !!scan.category && !!scan.subCategory && scan.desc.length >= 3;
  const { data: relatedHits = [], isFetching: relatedLoading } = useSearchRelatedCrQuery(
    {
      areaCode: scan.location || undefined,
      category: scan.category || undefined,
      subCategory: scan.subCategory || undefined,
      q: scan.desc || undefined,
      limit: 5,
      ...(isEdit && routeId ? { exclude: routeId } : {}),
    },
    { skip: step !== 1 || !canScanRelated },
  );
  // Only a same-location / same-category / same-sub-category CR is a real duplicate candidate.
  // (Text similarity is handled by the backend `q` scan.) Hits missing a field pass through.
  const relatedMatches = useMemo(() => {
    const same = (hit: unknown, want: string) =>
      !want || hit == null || String(hit) === String(want);
    return relatedHits.filter(
      (r) =>
        same(r.area_code, scan.location) &&
        same(r.category, scan.category) &&
        same(r.sub_category, scan.subCategory),
    );
  }, [relatedHits, scan.location, scan.category, scan.subCategory]);

  // Category options (no arg) and the selected category's sub-categories (?category=).
  const { data: crCategoryOptions } = useGetCrCategoriesQuery();
  const { data: crSubCategoryOptions } = useGetCrCategoriesQuery(
    { category: draft.category },
    { skip: !draft.category },
  );
  const setDraftField = (
    k: 'location' | 'category' | 'subCategory' | 'priority' | 'dueDate' | 'description',
    v: string,
  ) => taskFormik.setFieldValue(k, v);
  // Only surface a task error once the field is touched.
  const taskErrors: Record<string, string> = {};
  (Object.keys(taskFormik.errors) as (keyof typeof emptyDraft)[]).forEach((k) => {
    if (taskFormik.touched[k]) taskErrors[k] = taskFormik.errors[k] as string;
  });
  const addTask = () => taskFormik.submitForm();
  const editTask = (t: Task) => {
    setEditingTaskId(t.id);
    taskFormik.setValues({
      location: t.location,
      description: t.description,
      category: t.category,
      subCategory: t.subCategory,
      priority: t.priority,
      dueDate: t.dueDate,
      assignMembers: t.memberIds,
      teams: t.teams,
      refCr: t.refCr,
      isDuplicate: t.isDuplicate,
    });
    setCrRefSearch(t.refCr || '');
    taskFormik.setTouched({});
  };
  const cancelEdit = () => {
    setEditingTaskId(null);
    taskFormik.resetForm();
    setCrRefSearch('');
  };
  const removeTask = (t: Task) => {
    setTasks((prev) => prev.filter((x) => x.id !== t.id));
    if (editingTaskId === t.id) cancelEdit();
    setPendingTask(null);
  };

  // Step 3 (Assignment) — patch an issue, and manage its UI-only sub-tasks.
  const updateTask = (id: number, patch: Partial<Task>) =>
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const saveSubTask = (taskId: number, sub: SubTask) =>
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id !== taskId) return t;
        const exists = t.subTasks.some((s) => s.id === sub.id);
        return {
          ...t,
          subTasks: exists
            ? t.subTasks.map((s) => (s.id === sub.id ? sub : s))
            : [...t.subTasks, sub],
        };
      }),
    );
  const removeSubTask = (taskId: number, subId: number) =>
    updateTask(taskId, {
      subTasks: (tasks.find((t) => t.id === taskId)?.subTasks ?? []).filter((s) => s.id !== subId),
    });
  // Which issue's Assign Task modal is open (+ the sub-task being edited, if any).
  const [assignFor, setAssignFor] = useState<{ task: Task; sub?: SubTask } | null>(null);

  // Edit mode: prefill the requestor form + tasks once the request loads.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!editData || prefilled.current) return;
    prefilled.current = true;
    const d = (editData.details ?? {}) as Record<string, unknown>;
    const str = (x: unknown) => (typeof x === 'string' ? x : '');
    // Prefer the stored split parts; fall back to splitting the composed visitor_name.
    const nameParts = (editData.visitor_name || str(d.fullName)).trim().split(/\s+/);
    formik.setValues({
      firstName: str(d.firstName) || nameParts[0] || '',
      lastName: str(d.lastName) || nameParts.slice(1).join(' ') || '',
      email: str(d.email),
      phone: editData.visitor_phone || str(d.phone),
      receivedDate: editData.date_received || str(d.receivedDate),
      address: editData.visitor_address || str(d.address) || editData.location || '',
      district: str(d.district),
      description: editData.description || str(d.description),
    });
    setCrCode(editData.cr_code || `CR-${editData.id}`);
    if (Array.isArray(editData.assets)) setAssets(editData.assets);

    // Reference Person — best-effort prefill from the request's reference_person object.
    const rp = (d.reference_person ?? (editData as { reference_person?: unknown }).reference_person) as
      | {
          scope?: string;
          designation?: string;
          phone?: string;
          phones?: string[];
          user?: number | string;
          source?: string;
          name?: string;
          location?: string;
        }
      | undefined;
    if (rp) {
      const scope = rp.scope || 'inside';
      setRefScope(scope);
      setRefDesignation(rp.designation ?? '');
      const phones =
        Array.isArray(rp.phones) && rp.phones.length ? rp.phones : rp.phone ? [rp.phone] : [''];
      setRefPhones(phones.length ? phones : ['']);
      // Don't re-run the by-phone auto-fill for the saved number (keep the saved details).
      lastLookedUp.current = (phones[0] || '').replace(/\D/g, '');
      const uid = typeof rp.user === 'number' ? rp.user : '';
      const typedName = rp.name ?? (typeof rp.user === 'string' ? rp.user : '');
      if (scope === 'inside') {
        const source = rp.source ?? 'directory';
        setRefUserId(uid);
        setRefUserSource(typeof uid === 'number' ? source : '');
        setRefUserName(uid === '' ? typedName : '');
        setRefLocation(rp.location ?? '');
        if (typeof uid === 'number') {
          setPinnedRef({ value: `${source}-${uid}`, label: typedName || `Person ${uid}` });
          // The saved reference person may carry only the id — resolve its real name for display.
          if (!typedName) {
            runGetUser(uid)
              .unwrap()
              .then((u) => {
                const nm = userName(u);
                if (nm && nm !== '—') setPinnedRef({ value: `${source}-${uid}`, label: nm });
                if (u.directory) setRefDirectory(u.directory);
              })
              .catch(() => {});
          }
        }
      } else {
        setRefUserName(typedName);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  const prefilledTasks = useRef(false);
  useEffect(() => {
    if (!editData || prefilledTasks.current) return;
    prefilledTasks.current = true;
    setTasks(
      (editData.tasks ?? []).map((t, i) => {
        const memberIds = Array.isArray(t.assignMembers)
          ? t.assignMembers
          : (t.assignees ?? [])
              .map((a) => (typeof a === 'number' ? a : a?.id))
              .filter((v): v is number => typeof v === 'number');
        const teams: TeamEntry[] = Array.isArray(t.teams) ? t.teams : [];
        const names = [...memberIds, ...teams.map((x) => x.id)].map(nameOf).filter(Boolean);
        const assignLabel = names.join(', ') || t.assignee_name || t.department || '—';
        return {
          id: i + 1,
          status: t.status || 'To-Do',
          category: t.category || '',
          subCategory: crTaskSubCategory(t),
          priority: t.priority || '',
          description: t.description || '',
          location: (t as { location?: string }).location || '',
          dueDate: t.scheduled_date || t.due_date || t.dueDate || '',
          memberIds,
          teams,
          refCr: (t as { reference_cr?: string }).reference_cr || '',
          isDuplicate: !!(t as { is_duplicate?: boolean }).is_duplicate,
          assignLabel,
          // Nested sub-tasks come back as `subtasks` (read model) — map to the form shape.
          subTasks: (Array.isArray(t.subtasks) ? t.subtasks : []).map((s, si) => {
            const aid = s.assignees?.[0]?.id ?? s.assigned_user ?? '';
            return {
              id: s.id ?? si + 1,
              name: s.name || '',
              assigneeId: typeof aid === 'number' ? aid : '',
              assigneeName: s.assigned_user_name || s.assignees?.[0]?.full_name || '',
              dueDate: s.scheduled_date || '',
              status: s.task_status || 'To-Do',
              attachment: s.attachment ?? undefined,
            };
          }),
        };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  // Filter the local task list (no pagination).
  const visibleTasks = useMemo(() => {
    const q = taskSearch.trim().toLowerCase();
    return tasks.filter((t) => {
      if (q && !`${t.category} ${t.subCategory} ${t.location} ${t.description} ${t.assignLabel}`.toLowerCase().includes(q))
        return false;
      if (taskFilters.Category !== 'All' && t.category !== taskFilters.Category) return false;
      return true;
    });
  }, [tasks, taskSearch, taskFilters]);

  const clearTaskFilters = () => {
    setTaskSearch('');
    setTaskFilters(Object.fromEntries(TASK_FILTERS.map((f) => [f.label, 'All'])));
  };

  const goList = () => navigate('/user/citizen-requests');

  // Review step — the highest task priority drives the "Priority" pill.
  const reviewPriority = useMemo(() => {
    let best = -1;
    tasks.forEach((t) => {
      const rank = PRIORITIES.indexOf(t.priority);
      if (rank > best) best = rank;
    });
    return best >= 0 ? PRIORITIES[best] : '';
  }, [tasks]);

  // Build the Step-1 payload from the requestor form.
  const step1Body = () => ({
    // Compose the full name from the split parts; keep the parts in `details` for edit prefill.
    fullName:
      [form.firstName.trim(), form.lastName.trim()].filter(Boolean).join(' ') || undefined,
    firstName: form.firstName.trim() || undefined,
    lastName: form.lastName.trim() || undefined,
    email: form.email.trim() || undefined,
    phone: form.phone.trim() || undefined,
    receivedDate: form.receivedDate || undefined,
    address: form.address.trim() || undefined,
    district: form.district || undefined,
    // CR-level description is now per-issue; fall back to the first issue's description.
    description: form.description.trim() || tasks[0]?.description || undefined,
    reference_person: {
      scope: refScope,
      designation: refDesignation.trim() || undefined,
      // One or more contact numbers — `phone` keeps the first for backward compatibility.
      ...(() => {
        const phones = refPhones.map((p) => p.trim()).filter(Boolean);
        return phones.length ? { phone: phones[0], phones } : {};
      })(),
      ...(refScope === 'inside'
        ? {
            // A picked person sends its id + source; a typed custom value sends a name.
            ...(refUserId !== ''
              ? { user: refUserId, source: refUserSource || undefined }
              : { name: refUserName.trim() || undefined }),
            location: refLocation || undefined,
          }
        : { name: refUserName.trim() || undefined }),
    },
    ...(assets.length ? { assets } : {}),
  });

  // Map the local tasks into the request payload shape.
  const taskPayload = () =>
    tasks.map((t) => ({
      category: t.category,
      subCategory: t.subCategory || undefined,
      priority: t.priority || undefined,
      dueDate: t.dueDate || undefined,
      description: t.description || undefined,
      location: t.location || undefined,
      status: t.status || undefined,
      ...(t.refCr ? { reference_cr: t.refCr } : {}),
      ...(t.isDuplicate ? { is_duplicate: true } : {}),
      ...(t.memberIds.length ? { assignMembers: t.memberIds } : {}),
      ...(t.teams.length
        ? { teams: t.teams.map((x) => (x.primary ? { id: x.id, primary: true } : { id: x.id })) }
        : {}),
      ...(t.subTasks.length
        ? {
            subTasks: t.subTasks.map((s) => ({
              name: s.name,
              ...(s.assigneeId !== '' ? { assigneeId: Number(s.assigneeId) } : {}),
              ...(s.dueDate ? { dueDate: s.dueDate } : {}),
              ...(s.status ? { status: s.status } : {}),
              ...(s.attachment ? { attachment: s.attachment } : {}),
            })),
          }
        : {}),
    }));

  // Single create/update call carrying the nested tasks. status = 'draft' | 'complete'.
  const submitRequest = async (status: 'draft' | 'complete') => {
    setSaving(true);
    try {
      const body = { ...step1Body(), status, tasks: taskPayload() };
      if (isEdit) await updateRequest({ id: routeId as string, patch: body }).unwrap();
      else await createRequest(body).unwrap();
      message.success(status === 'draft' ? 'Saved as draft.' : 'Citizen request saved.');
      goList();
    } catch {
      message.error('Could not save the request. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Advance the wizard (validation only; the request is saved on the final step / Save Draft).
  const handleNext = async () => {
    if (step === 0) {
      // Basic Details — validate the requestor fields.
      const errs = await formik.validateForm();
      await formik.setTouched(
        Object.fromEntries(Object.keys(formik.initialValues).map((k) => [k, true])),
      );
      if (Object.keys(errs).length) return;
      setStep(1);
    } else if (step === 1) {
      // Issue Details — at least one issue must be added.
      if (tasks.length === 0) {
        message.error('Add at least one issue to continue.');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      // Assignment — every issue needs a priority + due date.
      if (tasks.some((t) => !t.priority || !t.dueDate)) {
        message.error('Set a priority and due date for every issue.');
        return;
      }
      setStep(3);
    } else {
      submitRequest('complete');
    }
  };

  return (
    <div className="flex flex-col gap-6 pb-24">
      {/* Header */}
      <div className="flex flex-col gap-4 rounded-xl bg-white px-8 py-5 shadow-card sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.6px]">
            <span className="text-[#64748b]">Citizen Requests</span>
            <span className="text-[#64748b]">&gt;</span>
            <span className="text-brand">{crCode || 'New Request'}</span>
          </div>
          <h1 className="text-2xl font-semibold leading-9 text-[#00173e]">
            {isEdit ? 'Edit Citizen Request' : 'Create Citizen Request'}
          </h1>
          <p className="text-sm text-ink-secondary">
            Log and assign new public grievances or service requests to the appropriate departments.
          </p>
        </div>
        <button
          type="button"
          onClick={goList}
          className="shrink-0 rounded-full border border-[#e2e8f0] bg-white px-4 py-2 text-sm font-medium text-[#00173e] transition-colors hover:bg-brand-shade"
        >
          Discard Changes
        </button>
      </div>

      {/* Stepper — steps are clickable once the request exists (edit mode / after Step 1). */}
      <div className="flex items-start px-2 pb-9 pt-1">
        {STEPS.map((label, i) => {
          const active = i === step;
          const done = i < step;
          const navigable = isEdit && i !== step;
          const labelAnchor =
            i === 0 ? 'left-0' : i === STEPS.length - 1 ? 'right-0' : 'left-1/2 -translate-x-1/2';
          return (
            <Fragment key={label}>
              <button
                type="button"
                disabled={!navigable}
                onClick={() => setStep(i)}
                className={[
                  'relative flex shrink-0 flex-col items-center',
                  navigable ? 'cursor-pointer' : 'cursor-default',
                ].join(' ')}
              >
                <span
                  className={[
                    'flex size-10 items-center justify-center rounded-full text-base',
                    active || done
                      ? 'bg-brand text-white shadow-[0px_0px_0px_4px_#faf8ff,0px_1px_2px_0px_rgba(0,0,0,0.05)]'
                      : 'border-2 border-[#d1e2ff] bg-[#eef4ff] text-[#424655]',
                  ].join(' ')}
                >
                  {i + 1}
                </span>
                <span
                  className={[
                    'absolute top-12 whitespace-nowrap text-base',
                    labelAnchor,
                    active || done ? 'text-brand' : 'text-[#424655]',
                  ].join(' ')}
                >
                  {label}
                </span>
              </button>
              {i < STEPS.length - 1 && (
                <span
                  className={[
                    'mt-5 h-0.5 flex-1 rounded-full',
                    i < step ? 'bg-brand' : 'bg-[#d1e2ff]',
                  ].join(' ')}
                />
              )}
            </Fragment>
          );
        })}
      </div>

      {step === 0 && (
        <>
          {/* Requestor Information */}
          <SectionCard
            icon={<PersonIcon className="size-5 text-brand" />}
            iconWrap="size-8 bg-[#eff6ff]"
            title="Requestor Information"
            action={
              <button
                type="button"
                onClick={() => setShowCitizenSearch((o) => !o)}
                className="text-sm font-semibold text-brand hover:underline"
              >
                Search Existing Citizen
              </button>
            }
          >
            <div className="flex flex-col gap-6">
              {showCitizenSearch && (
                <Select
                  className="field-muted w-full"
                  placeholder="Search an existing citizen by name or phone…"
                  showSearch
                  allowClear
                  filterOption={false}
                  value={null}
                  onSearch={setCitizenSearch}
                  onChange={(id) => {
                    const c = (citizenHits?.items ?? []).find((x) => x.id === id);
                    if (c) prefillFromCitizen(c);
                  }}
                  options={(citizenHits?.items ?? []).map((c) => ({
                    value: c.id,
                    label: `${c.visitor_name || 'Unknown'}${c.visitor_phone ? ` · ${c.visitor_phone}` : ''}`,
                  }))}
                  notFoundContent={citizenSearchQ ? 'No matches' : 'Type to search'}
                />
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <label className="flex flex-col gap-1.5">
                  <span className={LABEL}>
                    First Name<span className="text-[#ef4444]"> *</span>
                  </span>
                  <input
                    className={[INPUT, errors.firstName ? '!border-[#ef4444]' : ''].join(' ')}
                    placeholder="John"
                    value={form.firstName}
                    onChange={(e) => set('firstName', e.target.value)}
                  />
                  {errors.firstName && (
                    <span className="text-xs font-medium text-[#ef4444]">{errors.firstName}</span>
                  )}
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className={LABEL}>
                    Last Name<span className="text-[#ef4444]"> *</span>
                  </span>
                  <input
                    className={[INPUT, errors.lastName ? '!border-[#ef4444]' : ''].join(' ')}
                    placeholder="Doe"
                    value={form.lastName}
                    onChange={(e) => set('lastName', e.target.value)}
                  />
                  {errors.lastName && (
                    <span className="text-xs font-medium text-[#ef4444]">{errors.lastName}</span>
                  )}
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className={LABEL}>Email ID</span>
                  <input
                    className={[INPUT, errors.email ? '!border-[#ef4444]' : ''].join(' ')}
                    type="email"
                    placeholder="john.doe@gmail.com"
                    value={form.email}
                    onChange={(e) => set('email', e.target.value)}
                  />
                  {errors.email && (
                    <span className="text-xs font-medium text-[#ef4444]">{errors.email}</span>
                  )}
                </label>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <label className="flex flex-col gap-1.5">
                  <span className={LABEL}>
                    Phone Number<span className="text-[#ef4444]"> *</span>
                  </span>
                  <input
                    className={[INPUT, errors.phone ? '!border-[#ef4444]' : ''].join(' ')}
                    placeholder="+91"
                    value={form.phone}
                    onChange={(e) => set('phone', e.target.value)}
                  />
                  {errors.phone && (
                    <span className="text-xs font-medium text-[#ef4444]">{errors.phone}</span>
                  )}
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className={LABEL}>Address</span>
                  <input
                    className={INPUT}
                    placeholder="Street, landmark.."
                    value={form.address}
                    onChange={(e) => set('address', e.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className={LABEL}>Request Received Date</span>
                  <DatePicker
                    className="field-muted w-full"
                    status={errors.receivedDate ? 'error' : undefined}
                    format="DD/MM/YYYY"
                    placeholder="dd/mm/yyyy"
                    value={form.receivedDate ? dayjs(form.receivedDate) : null}
                    onChange={(d) => set('receivedDate', d ? d.format('YYYY-MM-DD') : '')}
                  />
                  {errors.receivedDate && (
                    <span className="text-xs font-medium text-[#ef4444]">{errors.receivedDate}</span>
                  )}
                </label>
              </div>
            </div>
          </SectionCard>

          {/* Reference Person */}
          <SectionCard
            icon={<DdReferenceIcon className="size-5 text-brand" />}
            iconWrap="size-8 bg-[#eef4ff]"
            title="Reference Person"
          >
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="flex flex-wrap items-center gap-6 md:col-span-2">
                {REFERENCE_SCOPES.map((o) => (
                  <label key={o.key} className="flex cursor-pointer items-center gap-2 text-sm text-[#00173e]">
                    <input
                      type="radio"
                      name="refScope"
                      className="size-4 accent-brand"
                      checked={refScope === o.key}
                      onChange={() => setRefScope(o.key)}
                    />
                    {o.label}
                  </label>
                ))}
              </div>

              {/* Select Reference Person — dropdown within the constituency, free-text otherwise */}
              {refScope === 'inside' ? (
                <label className="order-2 flex flex-col gap-1.5">
                  <span className={LABEL}>Search Reference Person</span>
                  <Select
                    className="field-muted w-full"
                    placeholder="Search or type a reference person"
                    showSearch
                    allowClear
                    filterOption={false}
                    onSearch={setRefPersonInput}
                    value={
                      // Prefer the exact selected key (the option's value may be a server
                      // `key` that differs from `${source}-${id}`; a mismatch would blank
                      // the input). Fall back to the reconstructed key, then a custom name.
                      pinnedRef?.value ??
                      (refUserId !== ''
                        ? `${refUserSource || 'directory'}-${refUserId}`
                        : refUserName || undefined)
                    }
                    onChange={(v, option) => {
                      // Selecting a person only sets the reference name/id. Designation,
                      // location and the contact number come from the phone auto-fill (and stay
                      // editable) — selecting here no longer overwrites them.
                      const opt = Array.isArray(option) ? undefined : option;
                      const refId = opt && typeof opt.refId === 'number' ? opt.refId : undefined;
                      if (refId !== undefined) {
                        setRefUserId(refId);
                        setRefUserSource((opt?.source as string) ?? 'directory');
                        setRefUserName('');
                        setPinnedRef({ value: String(v), label: (opt?.label as string) ?? '' });
                      } else {
                        setRefUserId('');
                        setRefUserSource('');
                        setRefUserName(typeof v === 'string' ? v : '');
                        setPinnedRef(null);
                      }
                    }}
                    options={(() => {
                      const opts: {
                        value: string;
                        label: string;
                        source?: string;
                        refId?: number;
                        designation?: string;
                        location?: string;
                        phones?: string[];
                      }[] = [...refPersonOptions];
                      // Pin the selected person so its label always resolves after a search reloads.
                      if (pinnedRef && !opts.some((o) => o.value === pinnedRef.value)) {
                        opts.unshift(pinnedRef);
                      }
                      const q = refPersonInput.trim();
                      // Offer the typed text as a custom option when it isn't an existing person.
                      if (q && !opts.some((o) => String(o.label).toLowerCase() === q.toLowerCase())) {
                        opts.push({ value: q, label: `Use “${q}”` });
                      }
                      // Keep a previously-typed custom name selectable/visible.
                      if (refUserName && !opts.some((o) => o.value === refUserName)) {
                        opts.unshift({ value: refUserName, label: refUserName });
                      }
                      return opts;
                    })()}
                    notFoundContent={organization == null ? 'No organization' : null}
                  />
                </label>
              ) : (
                <label className="order-2 flex flex-col gap-1.5">
                  <span className={LABEL}>Enter Reference Person</span>
                  <input
                    className={INPUT}
                    placeholder="Reference person name"
                    value={refUserName}
                    onChange={(e) => setRefUserName(e.target.value)}
                  />
                </label>
              )}

              <label className="order-3 flex flex-col gap-1.5">
                <span className={LABEL}>Select Designation</span>
                {/* Free-text with suggestions: a reference person can hold any designation
                    (especially one not in the directory), so allow typing a custom value while
                    still offering the field_options as suggestions. */}
                <AutoComplete
                  className="field-muted w-full"
                  placeholder="Select or type a designation"
                  allowClear
                  value={refDesignation || undefined}
                  onChange={(v) => setRefDesignation((v as string) ?? '')}
                  options={refDesignationOptions}
                  filterOption={(input, option) =>
                    String(option?.label ?? option?.value ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </label>

              {refScope === 'inside' && (
                <label className="order-4 flex flex-col gap-1.5">
                  <span className={LABEL}>Select Location</span>
                  {/* Location source follows the reference person's directory (from by-phone):
                      party → org-node hierarchy; bureaucracy → EC tree; else → location tree. */}
                  {refDirectory === 'party' ? (
                    <PartyLocationDropdown
                      organization={organization}
                      value={refLocation}
                      placeholder="Location"
                      onChange={(key) => setRefLocation(key)}
                    />
                  ) : (
                    <LocationCascade
                      organization={organization}
                      value={refLocation}
                      placeholder="Location"
                      source={refDirectory === 'bureaucracy' ? 'ec-tree' : 'location-tree'}
                      onChange={(sel) => setRefLocation(sel ? String(sel.value) : '')}
                    />
                  )}
                </label>
              )}

              <div className="order-1 flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className={LABEL}>Contact Number</span>
                  <button
                    type="button"
                    onClick={() => setRefPhones((p) => [...p, ''])}
                    className="text-xs font-semibold text-brand hover:underline"
                  >
                    + Add Another Number
                  </button>
                </div>
                <div className="flex flex-col gap-2">
                  {refPhones.map((p, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        inputMode="numeric"
                        placeholder="+91"
                        value={p}
                        onChange={(e) =>
                          setRefPhones((prev) => prev.map((x, idx) => (idx === i ? e.target.value : x)))
                        }
                        className={INPUT}
                      />
                      {refPhones.length > 1 && (
                        <button
                          type="button"
                          aria-label="Remove number"
                          onClick={() =>
                            setRefPhones((prev) => {
                              const next = prev.filter((_, idx) => idx !== i);
                              return next.length ? next : [''];
                            })
                          }
                          className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#ef4444] transition-colors hover:bg-red-50"
                        >
                          <TrashIcon className="size-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {refPhones.some((p) => p.trim() && !/^\d{10}$/.test(p.replace(/\s+/g, ''))) && (
                  <span className="text-xs font-medium text-[#ef4444]">
                    Enter valid 10-digit mobile numbers
                  </span>
                )}
              </div>
            </div>
          </SectionCard>
        </>
      )}

      {step === 1 && (
        <>
          {/* Issue card — create/edit one issue (a task) */}
          <SectionCard
            icon={<CrDescDocIcon className="size-5 text-[#cf4b00]" />}
            iconWrap="size-10 bg-[#cf4b00]/10"
            title="Issue"
            subtitle="Provide comprehensive details and supporting evidence."
            action={
              <button
                type="button"
                onClick={cancelEdit}
                className="flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
              >
                <PlusIcon className="size-3" />
                Add Another Issue
              </button>
            }
          >
            <div className="flex flex-col gap-5">
              {/* Category / Sub-Category */}
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <label className="flex flex-col gap-1.5">
                  <span className={LABEL}>
                    Category<span className="text-[#ef4444]"> *</span>
                  </span>
                  <Select
                    className="field-muted w-full"
                    status={taskErrors.category ? 'error' : undefined}
                    placeholder="Select category..."
                    showSearch
                    optionFilterProp="label"
                    value={draft.category || undefined}
                    onChange={(v) => {
                      setDraftField('category', v);
                      setDraftField('subCategory', '');
                    }}
                    options={crCategoryOptions ?? []}
                  />
                  {taskErrors.category && (
                    <span className="text-xs font-medium text-[#ef4444]">{taskErrors.category}</span>
                  )}
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className={LABEL}>
                    Sub-Category<span className="text-[#ef4444]"> *</span>
                  </span>
                  <Select
                    className="field-muted w-full"
                    status={taskErrors.subCategory ? 'error' : undefined}
                    placeholder="Select sub-category..."
                    showSearch
                    optionFilterProp="label"
                    disabled={!draft.category}
                    value={draft.subCategory || undefined}
                    onChange={(v) => setDraftField('subCategory', v)}
                    options={crSubCategoryOptions ?? []}
                  />
                  {taskErrors.subCategory && (
                    <span className="text-xs font-medium text-[#ef4444]">
                      {taskErrors.subCategory}
                    </span>
                  )}
                </label>
              </div>

              {/* SEC-Issue Location */}
              <label className="flex flex-col gap-1.5">
                <span className={LABEL}>
                  SEC- Issue Location<span className="text-[#ef4444]"> *</span>
                </span>
                <LocationCascade
                  organization={organization}
                  value={draft.location}
                  placeholder="Select issue location"
                  invalid={!!taskErrors.location}
                  onChange={(sel) => setDraftField('location', sel ? String(sel.value) : '')}
                />
                {taskErrors.location && (
                  <span className="text-xs font-medium text-[#ef4444]">{taskErrors.location}</span>
                )}
              </label>

              {/* Issue description + Related Issue panel */}
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <span className={LABEL}>
                      Issue<span className="text-[#ef4444]"> *</span>
                    </span>
                    <span className="text-[11px] tracking-[0.55px] text-[#727787]">
                      {draft.description.trim() ? draft.description.trim().split(/\s+/).length : 0} /
                      500 words
                    </span>
                  </div>
                  <textarea
                    className={[
                      INPUT,
                      'h-[280px] resize-none pt-3',
                      taskErrors.description ? '!border-[#ef4444]' : '',
                    ].join(' ')}
                    placeholder="Describe the issue in detail. Include when it started, how it affects the community, and any previous attempts to resolve it..."
                    value={draft.description}
                    onChange={(e) => setDraftField('description', e.target.value)}
                  />
                  {taskErrors.description && (
                    <span className="text-xs font-medium text-[#ef4444]">
                      {taskErrors.description}
                    </span>
                  )}
                </div>

                {/* Related Issue — live duplicate scan (/citizen-request/related/) */}
                <div className="flex flex-col gap-3 rounded-xl border border-[#e2e8f0] bg-[#f8faff] p-4">
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold text-[#00173e]">Related Issue</h4>
                      <span className="rounded-full bg-[#dbeafe] px-2 py-0.5 text-[11px] font-semibold text-brand">
                        {relatedLoading ? 'scanning…' : `${relatedMatches.length} found`}
                      </span>
                    </div>
                    <p className="text-[11px] leading-4 text-[#64748b]">
                      We scan your Issue for overlap with existing issues. Mark one as a duplicate and
                      we will link your report to it instead of opening a new issue.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2.5">
                    {!canScanRelated && (
                      <p className="rounded-lg border border-dashed border-[#e2e8f0] bg-white px-3 py-4 text-center text-xs text-[#94a3b8]">
                        Fill in the Location, Category, Sub-Category and Description to scan for
                        related issues.
                      </p>
                    )}
                    {canScanRelated && !relatedLoading && relatedMatches.length === 0 && (
                      <p className="rounded-lg border border-dashed border-[#e2e8f0] bg-white px-3 py-4 text-center text-xs text-[#94a3b8]">
                        No related issues found.
                      </p>
                    )}
                    {relatedMatches.map((r) => {
                      const code = r.cr_code || `#REQ-${r.id}`;
                      const ward = r.area_name || r.location_name || r.area_code || '';
                      const repeats = r.repeat_count ?? 0;
                      const high = repeats >= 5 || /high/i.test(r.letter_repeat_frequency || '');
                      const freq =
                        repeats > 0
                          ? `Repeated ${repeats} times${high ? ' (High)' : ''}`
                          : r.letter_repeat_frequency || '';
                      const marked = draft.refCr === code;
                      return (
                        <div
                          key={r.id}
                          className={[
                            'flex flex-col gap-2 rounded-lg border bg-white p-3',
                            marked ? 'border-brand' : 'border-[#e2e8f0]',
                          ].join(' ')}
                        >
                          <div className="flex flex-wrap items-center gap-2 text-[11px]">
                            <span className="rounded bg-[#eff6ff] px-1.5 py-0.5 font-bold text-brand">
                              {code}
                            </span>
                            {r.category && (
                              <span className="font-medium text-[#334155]">{r.category}</span>
                            )}
                            {ward && <span className="text-[#94a3b8]">{ward}</span>}
                          </div>
                          {(freq || marked) && (
                            <div className="flex flex-wrap items-center gap-2">
                              {freq && (
                                <span
                                  className={[
                                    'rounded px-1.5 py-0.5 text-[10px] font-semibold',
                                    high ? 'bg-[#fef2f2] text-[#dc2626]' : 'bg-[#f1f5f9] text-[#475569]',
                                  ].join(' ')}
                                >
                                  {freq}
                                </span>
                              )}
                              {marked && (
                                <span className="rounded bg-[#eff6ff] px-1.5 py-0.5 text-[10px] font-semibold text-brand">
                                  Linked Duplicate
                                </span>
                              )}
                            </div>
                          )}
                          {(r.description || r.subject) && (
                            <p className="text-xs leading-4 text-[#475569]">
                              {r.description || r.subject}
                            </p>
                          )}
                          <div className="flex items-center justify-end gap-3 border-t border-[#f1f5f9] pt-2">
                            {marked ? (
                              <>
                                <span className="flex items-center gap-1 rounded-md bg-brand px-2.5 py-1 text-[11px] font-semibold text-white">
                                  <CrTeamIcon className="size-3" />
                                  Marked as Duplicate
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    taskFormik.setFieldValue('refCr', '');
                                    taskFormik.setFieldValue('isDuplicate', false);
                                  }}
                                  className="text-[11px] font-medium text-[#64748b] hover:text-ink"
                                >
                                  Undo
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => {
                                  taskFormik.setFieldValue('refCr', code);
                                  taskFormik.setFieldValue('isDuplicate', true);
                                }}
                                className="flex items-center gap-1 rounded-md border border-[#e2e8f0] px-2.5 py-1 text-[11px] font-medium text-[#334155] transition-colors hover:bg-brand-shade/60"
                              >
                                <LinkIcon className="size-3" />
                                Mark as Duplicate
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Supporting Evidence */}
              <div className="flex flex-col gap-2">
                <span className={FLABEL}>Supporting Evidence</span>
                <label
                  className="flex min-h-[160px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#d1e2ff] bg-white p-6 text-center transition-colors hover:border-brand/40"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    onFiles(e.dataTransfer.files);
                  }}
                >
                  <input
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      onFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  <span className="flex size-12 items-center justify-center rounded-full bg-[#0057cd]/10">
                    <CrUploadCloudIcon className="h-4 w-[22px] text-[#0057cd]" />
                  </span>
                  <span className="text-[15px] font-semibold text-[#191b24]">
                    {uploading ? 'Uploading…' : 'Upload Photos / Documents'}
                  </span>
                  <span className="text-xs text-[#424655]">
                    Drag &amp; drop files here, or click to browse
                  </span>
                  <span className="flex items-center gap-2">
                    {['JPG', 'PNG', 'PDF'].map((t) => (
                      <span
                        key={t}
                        className="rounded bg-[#e1e2ee] px-2 py-1 text-[10px] uppercase text-[#424655]"
                      >
                        {t}
                      </span>
                    ))}
                  </span>
                  <span className="text-[10px] text-[#727787]">Max size: 10MB per file</span>
                </label>
                {assets.length > 0 && (
                  <div className="mt-1 grid grid-cols-6 gap-2">
                    {assets.map((a, idx) => (
                      <div
                        key={`${a.file_url}-${idx}`}
                        className="group relative overflow-hidden rounded-lg border border-[#e2e8f0] bg-[#f8fafc]"
                      >
                        {isImageAsset(a) ? (
                          <img
                            src={a.file_url}
                            alt={a.name || 'evidence'}
                            className="h-20 w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-20 flex-col items-center justify-center gap-1 p-2 text-center">
                            <CrDescDocIcon className="size-5 text-[#64748b]" />
                            <span className="line-clamp-2 text-[10px] text-[#424655]">
                              {a.name || 'file'}
                            </span>
                          </div>
                        )}
                        <button
                          type="button"
                          aria-label="Remove file"
                          onClick={() => removeAsset(idx)}
                          className="absolute right-1 top-1 flex size-5 items-center justify-center rounded-full bg-black/50 text-xs leading-none text-white opacity-0 transition-opacity group-hover:opacity-100"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit issue */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={addTask}
                  disabled={addingTask}
                  className="rounded-lg border border-[#0061FF] bg-transparent px-6 py-2 text-center text-xs font-medium leading-4 text-[#0061FF] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] transition-colors hover:bg-[#0061FF]/5 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {editingTaskId ? 'Save Issue' : 'Submit'}
                </button>
              </div>
            </div>
          </SectionCard>

          {/* Added Issues */}
          <div className="rounded-xl border border-[#e2e8f0] bg-white p-6 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold uppercase tracking-[0.5px] text-[#334155]">
                  Added Issues
                </h3>
                <span className="rounded-full bg-[#eff6ff] px-2 py-0.5 text-[11px] font-semibold text-brand">
                  {tasks.length} {tasks.length === 1 ? 'issue' : 'issues'}
                </span>
              </div>
              <span className="text-xs text-[#94a3b8]">
                Tasks will be created and tracked individually
              </span>
            </div>

            {tasks.length === 0 ? (
              <p className="mt-4 rounded-lg border border-dashed border-[#e2e8f0] bg-[#f8fafc] p-6 text-center text-sm text-ink-secondary">
                No issues added yet. Fill the form above and click Submit.
              </p>
            ) : (
              <div className="mt-4 flex flex-col gap-3">
                {tasks.map((t, i) => (
                  <div
                    key={t.id}
                    className="flex items-start justify-between gap-4 rounded-xl border border-[#e2e8f0] bg-white p-4"
                  >
                    <div className="flex min-w-0 flex-col gap-1.5">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded bg-[#eff6ff] px-2 py-0.5 font-bold text-brand">
                          ISSUE #{i + 1}
                        </span>
                        {t.category && (
                          <span className="font-medium text-[#334155]">{t.category}</span>
                        )}
                        {t.subCategory && (
                          <>
                            <span className="text-[#cbd5e1]">•</span>
                            <span className="text-[#64748b]">{t.subCategory}</span>
                          </>
                        )}
                        {t.location && (
                          <>
                            <span className="text-[#cbd5e1]">•</span>
                            <span className="text-[#64748b]">{t.location}</span>
                          </>
                        )}
                      </div>
                      {t.description && (
                        <p className="truncate text-sm text-[#334155]">{t.description}</p>
                      )}
                      <p className="text-[11px] text-[#94a3b8]">Action: Primary Master Ticket</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        aria-label="Edit issue"
                        onClick={() => editTask(t)}
                        className="flex size-8 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-brand-shade hover:text-brand"
                      >
                        <CrEditIcon className="size-4" />
                      </button>
                      <button
                        type="button"
                        aria-label="Delete issue"
                        onClick={() => setPendingTask(t)}
                        className="flex size-8 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-[#fee2e2] hover:text-[#dc2626]"
                      >
                        <CrDeleteIcon className="size-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {step === 2 && (
        <div className="rounded-xl bg-white p-6 shadow-card">
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-semibold text-[#00173e]">Create Task &amp; Assign</h2>
            <p className="text-sm text-ink-secondary">
              Assign the issue(s) to one or more Org users. Set priority and due date for completion.
            </p>
          </div>

          {tasks.length === 0 ? (
            <p className="mt-5 rounded-lg border border-dashed border-[#e2e8f0] bg-[#f8fafc] p-8 text-center text-sm text-ink-secondary">
              No issues yet. Go back to Issue Details and add at least one issue.
            </p>
          ) : (
            <div className="mt-5 flex flex-col gap-5">
              {tasks.map((t, i) => (
                <div key={t.id} className="rounded-xl border border-[#e2e8f0] bg-[#fbfcff] p-5">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-sm font-semibold text-[#00173e]">
                      Issue {i + 1}: {t.description || t.category || 'Untitled issue'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setAssignFor({ task: t })}
                      className="flex shrink-0 items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
                    >
                      <PlusIcon className="size-3" />
                      Assign Task
                    </button>
                  </div>

                  {/* Category / Sub-Category (read-only) + Priority + Due Date */}
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
                    <label className="flex flex-col gap-1.5">
                      <span className={LABEL}>Category</span>
                      <input className={`${INPUT} bg-[#f1f5f9]`} value={t.category} readOnly />
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className={LABEL}>Sub Category</span>
                      <input className={`${INPUT} bg-[#f1f5f9]`} value={t.subCategory} readOnly />
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className={LABEL}>
                        Priority<span className="text-[#ef4444]"> *</span>
                      </span>
                      <Select
                        className="field-muted w-full"
                        placeholder="Priority"
                        value={t.priority || undefined}
                        onChange={(v) => updateTask(t.id, { priority: v ?? '' })}
                        options={PRIORITIES.map((p) => ({ value: p, label: p }))}
                        labelRender={(o) =>
                          o.value ? (
                            <span
                              className={`rounded px-2 py-0.5 text-xs font-semibold ${PRIORITY_TONE[o.value as string] ?? ''}`}
                            >
                              {o.label}
                            </span>
                          ) : (
                            <span className="text-[#9ca3af]">Priority</span>
                          )
                        }
                      />
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className={LABEL}>
                        Due Date<span className="text-[#ef4444]"> *</span>
                      </span>
                      <DatePicker
                        className="field-muted w-full"
                        format="DD MMM YYYY"
                        placeholder="Select date"
                        value={t.dueDate ? dayjs(t.dueDate) : null}
                        onChange={(d) => updateTask(t.id, { dueDate: d ? d.format('YYYY-MM-DD') : '' })}
                      />
                    </label>
                  </div>

                  {/* Task List */}
                  <div className="mt-4 flex flex-col gap-2">
                    <span className={LABEL}>
                      Task List<span className="text-[#ef4444]"> *</span>
                    </span>
                    {t.subTasks.length === 0 ? (
                      <p className="rounded-lg border border-dashed border-[#e2e8f0] bg-white px-4 py-5 text-center text-xs text-[#94a3b8]">
                        No tasks assigned yet. Use “Assign Task” to add one.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {t.subTasks.map((s, si) => (
                          <div
                            key={s.id}
                            className="flex items-center justify-between gap-3 rounded-lg border border-[#e2e8f0] bg-white p-3"
                          >
                            <div className="flex min-w-0 items-start gap-2.5">
                              <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-[#eef4ff] text-[10px] font-bold text-brand">
                                T{si + 1}
                              </span>
                              <div className="flex min-w-0 flex-col gap-1">
                                <span className="truncate text-sm font-medium text-[#00173e]">
                                  {s.name}
                                </span>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#64748b]">
                                  <span className="flex items-center gap-1">
                                    <PersonIcon className="size-3 shrink-0" />
                                    {s.assigneeName || nameOf(s.assigneeId) || '—'}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <DirCalendarIcon className="size-3 shrink-0" />
                                    {s.dueDate ? dayjs(s.dueDate).format('DD MMM YYYY') : '—'}
                                  </span>
                                  {s.attachment && (
                                    <span className="flex items-center gap-1 text-brand">
                                      <LinkIcon className="size-3 shrink-0" />
                                      <span className="max-w-[160px] truncate">
                                        {s.attachment.name}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-1.5">
                              <span
                                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusBadge(s.status)}`}
                              >
                                {s.status}
                              </span>
                              <button
                                type="button"
                                aria-label="Edit task"
                                onClick={() => setAssignFor({ task: t, sub: s })}
                                className="flex size-7 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-brand-shade hover:text-brand"
                              >
                                <CrEditIcon className="size-3.5" />
                              </button>
                              <button
                                type="button"
                                aria-label="Delete task"
                                onClick={() => removeSubTask(t.id, s.id)}
                                className="flex size-7 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-[#fee2e2] hover:text-[#dc2626]"
                              >
                                <CrDeleteIcon className="size-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="flex flex-col gap-5">
          {/* Basic Details */}
          <div className="rounded-xl bg-white p-6 shadow-card">
            <h3 className="text-xs font-bold uppercase tracking-[0.5px] text-brand">Basic Details</h3>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
              <label className="flex flex-col gap-1.5">
                <span className={LABEL}>Requestor Name</span>
                <input
                  className={INPUT}
                  readOnly
                  value={[form.firstName, form.lastName].filter(Boolean).join(' ') || '—'}
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className={LABEL}>Phone Number</span>
                <input className={INPUT} readOnly value={form.phone || '—'} />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className={LABEL}>Location</span>
                <input
                  className={INPUT}
                  readOnly
                  value={form.address || tasks[0]?.location || '—'}
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className={LABEL}>Created Date</span>
                <input
                  className={INPUT}
                  readOnly
                  value={
                    form.receivedDate
                      ? dayjs(form.receivedDate).format('DD MMM YYYY')
                      : dayjs().format('DD MMM YYYY, hh:mm A')
                  }
                />
              </label>
            </div>
          </div>

          {/* All Issues & Tasks Overview */}
          <div className="rounded-xl bg-white p-6 shadow-card">
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-0.5">
                <h3 className="text-base font-semibold text-[#00173e]">All Issues &amp; Tasks Overview</h3>
                <p className="text-sm text-ink-secondary">
                  Consolidated breakdown of public grievances and operational task distribution.
                </p>
              </div>
              <span className="shrink-0 rounded-full bg-[#eff6ff] px-3 py-1 text-[11px] font-semibold text-brand">
                {tasks.length} {tasks.length === 1 ? 'Issue' : 'Issues'} •{' '}
                {tasks.reduce((n, t) => n + t.subTasks.length, 0)} Tasks
              </span>
            </div>

            {tasks.length === 0 ? (
              <p className="mt-4 rounded-lg border border-dashed border-[#e2e8f0] bg-[#f8fafc] p-8 text-center text-sm text-ink-secondary">
                No issues added.
              </p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-[#e2e8f0] text-[11px] font-semibold uppercase tracking-[0.4px] text-[#64748b]">
                      <th className="py-2 pr-3 font-semibold">Issue &amp; Category</th>
                      <th className="py-2 pr-3 font-semibold">Task Details</th>
                      <th className="py-2 pr-3 font-semibold">Assigned Personnel</th>
                      <th className="py-2 pr-3 font-semibold">Due Date</th>
                      <th className="py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((t, i) => {
                      const rows = t.subTasks.length ? t.subTasks : [null];
                      return rows.map((s, si) => (
                        <tr key={`${t.id}-${s?.id ?? 'none'}`} className="border-b border-[#f1f5f9] align-top">
                          {si === 0 && (
                            <td className="py-3 pr-3" rowSpan={rows.length}>
                              <div className="flex flex-col gap-0.5">
                                <span className="text-sm font-semibold text-[#00173e]">
                                  Issue {i + 1}: {t.description || t.category || 'Untitled'}
                                </span>
                                <span className="text-xs text-[#64748b]">
                                  {[t.category, t.subCategory].filter(Boolean).join(' • ') || '—'}
                                </span>
                              </div>
                            </td>
                          )}
                          {s ? (
                            <>
                              <td className="py-3 pr-3">
                                <span className="flex items-start gap-2">
                                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded bg-[#eef4ff] text-[9px] font-bold text-brand">
                                    T{si + 1}
                                  </span>
                                  <span className="text-sm text-[#334155]">{s.name}</span>
                                </span>
                              </td>
                              <td className="py-3 pr-3">
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-[#00173e]">
                                    {s.assigneeName || nameOf(s.assigneeId) || '—'}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 pr-3 text-sm text-[#334155]">
                                {s.dueDate
                                  ? dayjs(s.dueDate).format('DD MMM YYYY')
                                  : t.dueDate
                                    ? dayjs(t.dueDate).format('DD MMM YYYY')
                                    : '—'}
                              </td>
                              <td className="py-3">
                                <span
                                  className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${statusBadge(s.status)}`}
                                >
                                  {s.status}
                                </span>
                              </td>
                            </>
                          ) : (
                            <td className="py-3 text-sm text-ink-secondary" colSpan={4}>
                              No tasks assigned.
                            </td>
                          )}
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="sticky bottom-0 -mx-4 -mb-24 flex items-center gap-4 border-t border-[#dbeafe] bg-[#eef4ff] px-6 py-4 sm:-mx-6">
        {step > 0 && (
          <button
            type="button"
            onClick={() => setStep((s) => s - 1)}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.6px] text-[#424655] hover:text-brand"
          >
            <ArrowLeftIcon className="size-3.5" />
            Back to {STEPS[step - 1]}
          </button>
        )}
        <div className="ml-auto flex items-center gap-4">
          <button
            type="button"
            disabled={saving}
            onClick={() => submitRequest('draft')}
            className="text-sm font-semibold text-[#00173e] hover:underline disabled:opacity-50"
          >
            Save Draft
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleNext}
            className="rounded-full bg-brand px-6 py-2 text-sm font-medium text-white shadow-[0px_1px_1px_rgba(0,0,0,0.05)] transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            {step === 3
              ? saving
                ? isEdit
                  ? 'Updating…'
                  : 'Submitting…'
                : isEdit
                  ? 'Update Request'
                  : 'Submit'
              : 'Continue'}
          </button>
        </div>
      </div>

      <ConfirmDeleteModal
        open={!!pendingTask}
        title="Delete Task"
        message={
          <>
            Are you sure you want to delete{' '}
            <strong>{pendingTask?.subCategory || pendingTask?.category || 'this task'}</strong>? This
            action cannot be undone.
          </>
        }
        onClose={() => setPendingTask(null)}
        onConfirm={() => pendingTask && removeTask(pendingTask)}
      />

      {/* Assign Task modal (Step 3) — add/edit a sub-task under an issue (UI-only). */}
      <AssignTaskModal
        open={!!assignFor}
        issueTitle={assignFor?.task.description || assignFor?.task.category || ''}
        memberOptions={memberOptions}
        initial={assignFor?.sub ?? null}
        onCancel={() => setAssignFor(null)}
        onSave={(sub) => {
          if (!assignFor) return;
          const withId: SubTask = sub.id
            ? sub
            : { ...sub, id: (assignFor.task.subTasks.at(-1)?.id ?? 0) + 1 };
          saveSubTask(assignFor.task.id, withId);
          setAssignFor(null);
        }}
      />
    </div>
  );
}
